<script setup lang="ts">
const { $containment } = useNuxtApp();
const { t } = useTexts();
const { openToaster } = useLayoutStore();
const { closeModal } = useLayoutStore();
const { temporaryPassword } = useModulesValidation();

const typedPassword = ref('');
const isLoading = ref(false);

const isPasswordLengthValid = computed<boolean>(() => {
  return typedPassword.value.length >= 5;
});

function handleSuccess(
  res: { status: number; data: { success: boolean; message: string } },
  successCallback?: (res: any) => void,
) {
  if (res.status.toString().startsWith('20') && res.data.success) {
    successCallback?.(res);
  } else {
    const message = res.data.message || t('auth.register.error.status_error');

    openToaster({
      message,
      type: 'error',
    });
  }
}

function handleError(err: any, fallbackMessageKey: string) {
  const errorMessage = err?.data?.message || t(fallbackMessageKey);

  if (err.status === 406) {
    closeModal();
  }

  openToaster({
    message: errorMessage,
    type: 'error',
  });
}

async function validatePassword() {
  if (!isPasswordLengthValid.value) {
    return;
  }

  isLoading.value = true;

  try {
    const { ApiService } = useApiService();

    const response = await ApiService.post('bff/users/check-password', {
      password: typedPassword.value,
    });

    handleSuccess(response, async () => {
      temporaryPassword.set(typedPassword.value);

      await $containment.solve({
        name: 'password',
        customValidator: () => response.data.success,
      });
    });
  } catch (err) {
    handleError(err, 'auth.register.error.response_error');

    temporaryPassword.clear();
  } finally {
    isLoading.value = false;
  }
}

async function onConfirmButtonClick() {
  await validatePassword();
}
</script>

<template>
  <div :class="$style.boxContainer">
    <BaseLoading
      class="!bg-bg-primary !absolute"
      :icon-loader="true"
      :show="isLoading"
    />

    <img
      alt="Email icon"
      aria-hidden="true"
      :class="$style.modalIcon"
      :src="getAssetsUrlForCDN('/assets/images/svg/complete.svg')"
    />

    <p :class="$style.title">
      {{ $t('verification.password.title') }}
    </p>

    <p
      :class="[$style.description, 'max-w-56']"
      v-html="$t('verification.password.desc')"
    />

    <BaseFormInput
      id="password"
      v-model="typedPassword"
      autocomplete="off"
      :label="$t('auth.login.password')"
      :name="'password'"
      :show-hide-pass="true"
    />

    <div :class="$style.buttonsContainer">
      <button
        :class="[$style.submitBtn, 'btn-primary']"
        :disabled="!isPasswordLengthValid"
        @click="onConfirmButtonClick"
      >
        {{ $t('verification.password.confirm_button') }}
        <NuxtIcon name="arrows/angle_right" />
      </button>
    </div>
  </div>
</template>

<style module lang="scss">
.boxContainer {
  @apply p-3 md:p-6 flex flex-col justify-center flex-1 text-texts text-center;

  .modalIcon {
    @apply w-28 h-28 mx-auto mb-5;
  }

  .title {
    @apply font-bold mb-3;
  }

  .description {
    @apply mb-4 inline-block mx-auto text-sm text-texts/80;

    strong {
      @apply text-primary/80;
    }
  }

  .buttonsContainer {
    @apply flex items-center justify-center gap-2 mx-auto mt-5;

    button {
      @apply rounded-md gap-1.5 flex items-center font-bold uppercase h-10 focus:outline-none focus:ring-4;
    }

    .submitBtn {
      @apply py-5;
    }
  }
}
</style>
